"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { createClient } from "@/lib/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BottomNav } from "@/components/bottom-nav";
import {
  Search,
  Loader2,
  MapPin,
  Clock,
  Shield,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Users,
} from "lucide-react";
import { TRADES } from "@/lib/constants";

interface BrowseWorker {
  id: string;
  display_name: string | null;
  suburb: string | null;
  avatar_url: string | null;
  trades: string[];
  experience_years: number | null;
  certifications: string[];
  availability: string | null;
  bio: string | null;
}

const ITEMS_PER_PAGE = 10;

export default function BrowseWorkersPage() {
  const supabase = createClient();
  const [workers, setWorkers] = useState<BrowseWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchSuburb, setSearchSuburb] = useState("");
  const [selectedTrade, setSelectedTrade] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"swipe" | "list">("swipe");
  const cardRef = useRef<HTMLDivElement>(null);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const startX = useRef(0);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const loadWorkers = useCallback(async () => {
    setLoading(true);

    // Step 1: Get profile IDs of real workers with completed onboarding
    const { data: validProfiles } = await supabase
      .from("profiles")
      .select("id, display_name, name, suburb, avatar_url")
      .eq("type", "worker")
      .eq("onboarding_completed", true);

    if (!validProfiles || validProfiles.length === 0) {
      setWorkers([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    const validProfileIds = validProfiles.map((p) => p.id);
    const profileMap = new Map(
      validProfiles.map((p) => [p.id, p])
    );

    // Step 2: Query worker_profiles only for valid (onboarded) workers
    let dataQuery = supabase
      .from("worker_profiles")
      .select(`
        profile_id,
        trades,
        experience_years,
        certifications,
        availability,
        bio
      `)
      .in("profile_id", validProfileIds)
      .order("experience_years", { ascending: false });

    // Apply trade filter
    if (selectedTrade) {
      dataQuery = dataQuery.contains("trades", [selectedTrade]);
    }

    const { data: workerProfiles } = await dataQuery;

    if (!workerProfiles || workerProfiles.length === 0) {
      setWorkers([]);
      setTotalCount(0);
      setLoading(false);
      return;
    }

    // Step 3: Enrich and filter
    let enriched: BrowseWorker[] = workerProfiles.map((wp) => {
      const profile = profileMap.get(wp.profile_id);
      return {
        id: wp.profile_id,
        display_name: profile?.name || profile?.display_name || null,
        suburb: profile?.suburb || null,
        avatar_url: profile?.avatar_url || null,
        trades: wp.trades || [],
        experience_years: wp.experience_years,
        certifications: wp.certifications || [],
        availability: wp.availability,
        bio: wp.bio,
      };
    });

    // Filter by suburb if specified
    if (searchSuburb.trim()) {
      const search = searchSuburb.trim().toLowerCase();
      enriched = enriched.filter(
        (w) => w.suburb && w.suburb.toLowerCase().includes(search)
      );
    }

    setTotalCount(enriched.length);

    // Client-side pagination
    const from = (currentPage - 1) * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE;
    enriched = enriched.slice(from, to);

    setWorkers(enriched);
    setCurrentIndex(0);
    setLoading(false);
  }, [supabase, currentPage, selectedTrade, searchSuburb]);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTrade, searchSuburb]);

  const currentWorker = workers[currentIndex];

  // Swipe handlers for card mode
  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    setSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swiping) return;
    const diff = e.touches[0].clientX - startX.current;
    setSwipeX(Math.max(-150, Math.min(diff, 150)));
  };

  const handleTouchEnd = () => {
    if (!swiping) return;
    setSwiping(false);

    if (swipeX > 100 && currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else if (swipeX < -100 && currentIndex < workers.length - 1) {
      setCurrentIndex((i) => i + 1);
    }

    setSwipeX(0);
  };

  const avatarInitial = (name: string | null) =>
    (name || "W")[0]?.toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Browse Workers
          </h1>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "swipe" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("swipe")}
              className="text-xs"
            >
              Cards
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="text-xs"
            >
              List
            </Button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="space-y-3 mb-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by suburb..."
                value={searchSuburb}
                onChange={(e) => setSearchSuburb(e.target.value)}
                className="pl-9 h-12 text-base"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              className="h-12 w-12 shrink-0"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-5 w-5" />
            </Button>
          </div>

          {/* Trade filter chips */}
          {showFilters && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedTrade(null)}
                className={`rounded-full px-3 py-2 text-sm font-medium transition-colors active:scale-95 ${
                  !selectedTrade
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                All Trades
              </button>
              {TRADES.map((trade) => (
                <button
                  key={trade}
                  onClick={() =>
                    setSelectedTrade(selectedTrade === trade ? null : trade)
                  }
                  className={`rounded-full px-3 py-2 text-sm font-medium transition-colors active:scale-95 ${
                    selectedTrade === trade
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {trade}
                </button>
              ))}
            </div>
          )}

          {/* Active filters display */}
          {(selectedTrade || searchSuburb) && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Filters:</span>
              {selectedTrade && (
                <Badge variant="secondary" className="gap-1">
                  {selectedTrade}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSelectedTrade(null)}
                  />
                </Badge>
              )}
              {searchSuburb && (
                <Badge variant="secondary" className="gap-1">
                  {searchSuburb}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSearchSuburb("")}
                  />
                </Badge>
              )}
              <span className="text-muted-foreground ml-auto">
                {totalCount} worker{totalCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : workers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Users className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-lg font-medium">No workers found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {selectedTrade || searchSuburb
                ? "Try adjusting your filters"
                : "Workers will appear here as they sign up"}
            </p>
          </div>
        ) : viewMode === "swipe" ? (
          /* Swipe/Card View */
          <div>
            {currentWorker && (
              <div className="relative">
                <Card
                  ref={cardRef}
                  className="touch-pan-y"
                  style={{
                    transform: `translateX(${swipeX}px) rotate(${swipeX * 0.05}deg)`,
                    transition: swiping ? "none" : "transform 0.2s ease-out",
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <CardContent className="p-6">
                    {/* Avatar & Name */}
                    <div className="flex items-center gap-4 mb-4">
                      {currentWorker.avatar_url ? (
                        <img
                          src={currentWorker.avatar_url}
                          alt={currentWorker.display_name || "Worker"}
                          className="h-16 w-16 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-2xl">
                          {avatarInitial(currentWorker.display_name)}
                        </div>
                      )}
                      <div>
                        <h2 className="text-lg font-bold">
                          {currentWorker.display_name || "Worker"}
                        </h2>
                        {currentWorker.suburb && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {currentWorker.suburb}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Trades */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {currentWorker.trades.map((trade) => (
                        <Badge key={trade} className="text-sm px-3 py-1">
                          {trade}
                        </Badge>
                      ))}
                    </div>

                    {/* Details */}
                    <div className="space-y-3 mb-4">
                      {currentWorker.experience_years !== null && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>
                            {currentWorker.experience_years} year
                            {currentWorker.experience_years !== 1 ? "s" : ""}{" "}
                            experience
                          </span>
                        </div>
                      )}
                      {currentWorker.availability && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="capitalize">
                            {currentWorker.availability}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Certifications */}
                    {currentWorker.certifications.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-1.5 text-sm font-medium mb-2">
                          <Shield className="h-4 w-4 text-primary" />
                          Certifications
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {currentWorker.certifications.map((cert) => (
                            <Badge
                              key={cert}
                              variant="outline"
                              className="text-xs"
                            >
                              {cert}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Bio */}
                    {currentWorker.bio && (
                      <p className="text-sm text-muted-foreground border-t pt-3">
                        {currentWorker.bio}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Navigation arrows */}
                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-12 w-12"
                    onClick={() =>
                      setCurrentIndex((i) => Math.max(0, i - 1))
                    }
                    disabled={currentIndex === 0}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>

                  <span className="text-sm text-muted-foreground">
                    {currentIndex + 1} of {workers.length}
                  </span>

                  <Button
                    variant="outline"
                    size="icon"
                    className="h-12 w-12"
                    onClick={() =>
                      setCurrentIndex((i) =>
                        Math.min(workers.length - 1, i + 1)
                      )
                    }
                    disabled={currentIndex === workers.length - 1}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {workers.map((worker) => (
              <Card key={worker.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {worker.avatar_url ? (
                      <img
                        src={worker.avatar_url}
                        alt={worker.display_name || "Worker"}
                        className="h-12 w-12 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-lg">
                        {avatarInitial(worker.display_name)}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold truncate">
                        {worker.display_name || "Worker"}
                      </h3>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {worker.trades.slice(0, 3).map((trade) => (
                          <Badge
                            key={trade}
                            variant="secondary"
                            className="text-xs"
                          >
                            {trade}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                        {worker.experience_years !== null && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {worker.experience_years}yr
                          </span>
                        )}
                        {worker.suburb && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {worker.suburb}
                          </span>
                        )}
                        {worker.availability && (
                          <span className="capitalize">
                            {worker.availability}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.max(1, p - 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      <BottomNav userType="contractor" />
    </div>
  );
}
